import { Button } from '@bambi/component-library/src/components/Button/Button';
import { useState } from 'react';

import { setRequestedTripRejectReasons } from '../../../dispatcher.slice';
import { OtherReasonDraftInput } from './OtherReasonDraftInput';
import { useGlobalRejectReasonOptions } from './useGlobalRejectReasonOptions';
import { useRejectTripIds } from './useRejectionReasonOptions';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DropdownMenuBasic } from 'src/common/DropdownMenu/DropdownMenuBasic';
import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import {
  composeOtherReason,
  parseOtherReason,
} from 'src/features/trip/management/cancel/otherReason';

export function GlobalRejectReasonMenu() {
  const dispatch = useAppDispatch();
  const tripIds = useRejectTripIds();
  const { options } = useGlobalRejectReasonOptions();
  const selectedTripReasons = useAppSelector(
    (state) => state.dispatcher.modals.requestedTripIdsToRejectToReasonCodes
  );
  const [globalReason, setGlobalReason] = useState('');
  const [globalOtherText, setGlobalOtherText] = useState('');

  // A global reason only makes sense across several trips; loading and
  // empty-intersection states render nothing too.
  if (tripIds.length < 2 || options.length === 0) {
    return null;
  }

  const applyGlobalReason = (reason: string) => {
    setGlobalReason(reason);
    const value =
      reason === 'other' ? composeOtherReason(globalOtherText) : reason;
    dispatch(
      setRequestedTripRejectReasons(
        Object.fromEntries(tripIds.map((tripId) => [tripId, value]))
      )
    );
  };

  // Fills every row still set to Other; rows switched to another reason
  // keep their choice. Committed on blur — see OtherReasonDraftInput.
  const commitGlobalOtherText = (text: string) => {
    // A focus/blur round-trip with no edit must not clobber per-row
    // refinements of the Other text.
    if (text === globalOtherText) {
      return;
    }
    setGlobalOtherText(text);
    const next = { ...selectedTripReasons };
    for (const tripId of tripIds) {
      const stored = next[tripId];
      if (stored && parseOtherReason(stored).reason === 'other') {
        next[tripId] = composeOtherReason(text);
      }
    }
    dispatch(setRequestedTripRejectReasons(next));
  };

  return (
    <div className="flex items-center justify-end gap-2 pb-2">
      <DropdownMenuBasic
        trigger={
          <div>
            <Button
              dataTestId="global-reject-reason-dropdown-trigger"
              variant="secondary"
              onClick={(e) => e.preventDefault()}
            >
              Select Global Rejection Reason
            </Button>
          </div>
        }
      >
        <div className="z-50">
          {options.map((option) => (
            <DropdownMenuItem
              onClick={() => applyGlobalReason(option.value)}
              key={option.value}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuBasic>
      {globalReason === 'other' ? (
        <div className="w-64">
          <OtherReasonDraftInput
            initialText={globalOtherText}
            ariaLabel="Other rejection reason for all trips"
            dataTestId="global-reject-other-reason-input"
            onCommit={commitGlobalOtherText}
          />
        </div>
      ) : null}
    </div>
  );
}
